import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ChevronRight, Truck, ShieldCheck, RotateCcw, Sparkles } from "lucide-react";

const banners = [
  {
    img: "https://images.unsplash.com/photo-1613915617430-8ab0fd7c6baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHx3b21lbiUyMGZhc2hpb24lMjBlZGl0b3JpYWwlMjBtb2RlbHxlbnwwfHx8fDE3ODE5NTY4MTd8MA&ixlib=rb-4.1.0&q=85",
    eyebrow: "NEW SEASON · 2026",
    title: "Where Style Meets Fire",
    sub: "Editorial-grade womenswear, crafted for India. Free shipping over ₹999.",
    cta: "Shop New Arrivals",
    link: "/products?new=true"
  },
  {
    img: "https://images.unsplash.com/photo-1571513800374-df1bbe650e56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHx3b21lbiUyMGZhc2hpb24lMjBlZGl0b3JpYWwlMjBtb2RlbHxlbnwwfHx8fDE3ODE5NTY4MTd8MA&ixlib=rb-4.1.0&q=85",
    eyebrow: "ETHNIC EDIT",
    title: "Heritage, reimagined.",
    sub: "Vibrant kurtas, sarees and contemporary fusion looks.",
    cta: "Explore Ethnic",
    link: "/products?category=Ethnic"
  }
];

export default function Home() {
  const [cats, setCats] = useState([]);
  const [trending, setTrending] = useState([]);
  const [best, setBest] = useState([]);
  const [arrivals, setArrivals] = useState([]);
  const [bIdx, setBIdx] = useState(0);

  useEffect(() => {
    (async () => {
      const [c, t, b, n] = await Promise.all([
        api.get("/categories"),
        api.get("/products?trending=true&limit=12"),
        api.get("/products?bestseller=true&limit=12"),
        api.get("/products?new=true&limit=12"),
      ]);
      setCats(c.data); setTrending(t.data); setBest(b.data); setArrivals(n.data);
    })();
  }, []);

  useEffect(() => { const i = setInterval(() => setBIdx(v => (v + 1) % banners.length), 6000); return () => clearInterval(i); }, []);

  return (
    <main>
      {/* Spin & Win promo strip */}
      <Link to="/spin" data-testid="home-spin-strip" className="block bg-gradient-to-r from-[#FF4500] via-[#FF6A2C] to-[#FFC85C] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold animate-pulse">
          <span>🔥 MEGA SPIN FESTIVAL — Pay ₹79, Win Guaranteed Rewards</span>
          <span className="hidden sm:inline">·</span>
          <span className="underline underline-offset-2">SPIN NOW →</span>
        </div>
      </Link>
      {/* Category strip */}
      <div className="border-b border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex gap-6 overflow-x-auto scrollbar-hide">
          {cats.map((c) => (
            <Link key={c.category_id} to={`/products?category=${encodeURIComponent(c.name)}`} data-testid={`cat-${c.slug}`}
              className="flex flex-col items-center gap-2 min-w-[78px] group">
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-slate-100 group-hover:border-[#004BCA] transition p-0.5 bg-white">
                <img src={c.image} alt={c.name} className="w-full h-full rounded-full object-cover" />
              </div>
              <span className="text-xs font-medium text-slate-700">{c.name}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Hero banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="relative rounded-3xl overflow-hidden h-[58vh] md:h-[70vh] bg-slate-900">
          {banners.map((b, i) => (
            <div key={i} className={`absolute inset-0 transition-opacity duration-1000 ${i === bIdx ? "opacity-100" : "opacity-0"}`}>
              <img src={b.img} alt={b.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 hero-overlay" />
              <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-16 max-w-2xl text-white">
                <span className="text-xs font-bold tracking-[0.25em] text-white/80 mb-3">{b.eyebrow}</span>
                <h1 className="font-display text-4xl md:text-6xl font-bold mb-4 fade-up">{b.title}</h1>
                <p className="text-base md:text-lg text-white/80 mb-6 max-w-md">{b.sub}</p>
                <Link to={b.link} data-testid={`hero-cta-${i}`} className="btn-fire inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-semibold w-fit">
                  {b.cta} <ChevronRight size={16} />
                </Link>
              </div>
            </div>
          ))}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
            {banners.map((_, i) => (
              <button key={i} onClick={() => setBIdx(i)} className={`h-1.5 rounded-full transition-all ${i === bIdx ? "w-8 bg-white" : "w-2 bg-white/50"}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { i: Truck, t: "Free Shipping", s: "On orders over ₹999" },
          { i: RotateCcw, t: "Easy Returns", s: "7-day return policy" },
          { i: ShieldCheck, t: "Secure Payments", s: "UPI, Cards, COD" },
          { i: Sparkles, t: "Curated by Stylefire", s: "100% Authentic" },
        ].map(({ i: Icon, t, s }) => (
          <div key={t} className="flex items-center gap-3 p-4 rounded-2xl border border-slate-100">
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#004BCA]/10 text-[#004BCA]"><Icon size={20} strokeWidth={1.7} /></span>
            <div><div className="text-sm font-semibold">{t}</div><div className="text-xs text-slate-500">{s}</div></div>
          </div>
        ))}
      </section>

      <Section title="Trending Now" cta="View all" link="/products?trending=true" items={trending} />
      <PromoGrid />
      <Section title="Best Sellers" cta="Shop best of" link="/products?bestseller=true" items={best} />
      <Section title="New Arrivals" cta="See new in" link="/products?new=true" items={arrivals} />
    </main>
  );
}

function Section({ title, cta, link, items }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-end justify-between mb-6">
        <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{title}</h2>
        <Link to={link} className="text-sm font-medium text-[#004BCA] inline-flex items-center gap-1">{cta} <ChevronRight size={16}/></Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {items.slice(0, 8).map(p => <ProductCard key={p.product_id} p={p} />)}
      </div>
    </section>
  );
}

function PromoGrid() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-3 gap-4">
      <Link to="/products?category=Dresses" className="relative rounded-3xl overflow-hidden h-64 group">
        <img src="https://images.unsplash.com/photo-1664076458686-3449062080ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHx3b21lbiUyMGZhc2hpb24lMjBlZGl0b3JpYWwlMjBtb2RlbHxlbnwwfHx8fDE3ODE5NTY4MTd8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
        <div className="absolute inset-0 bg-gradient-to-tr from-black/60 to-transparent" />
        <div className="absolute bottom-5 left-5 text-white">
          <div className="text-xs tracking-widest">UP TO 50% OFF</div>
          <div className="font-display text-2xl font-bold">Dresses Edit</div>
        </div>
      </Link>
      <Link to="/products?category=Footwear" className="relative rounded-3xl overflow-hidden h-64 group">
        <img src="https://images.unsplash.com/photo-1631542156377-473540702434?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHx3b21lbiUyMHN0eWxpc2glMjBmb290d2VhciUyMHNob2VzfGVufDB8fHx8MTc4MTk1NjgxN3ww&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
        <div className="absolute inset-0 bg-gradient-to-tr from-[#FF4500]/70 to-transparent" />
        <div className="absolute bottom-5 left-5 text-white">
          <div className="text-xs tracking-widest">STEP INTO IT</div>
          <div className="font-display text-2xl font-bold">Footwear from ₹1,399</div>
        </div>
      </Link>
      <Link to="/products?bestseller=true" className="relative rounded-3xl overflow-hidden h-64 group">
        <img src="https://images.pexels.com/photos/7318906/pexels-photo-7318906.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
        <div className="absolute inset-0 bg-gradient-to-tr from-[#004BCA]/70 to-transparent" />
        <div className="absolute bottom-5 left-5 text-white">
          <div className="text-xs tracking-widest">LOVED BY 10,000+</div>
          <div className="font-display text-2xl font-bold">Best Sellers</div>
        </div>
      </Link>
    </section>
  );
}
